import type { MatrixError } from 'matrix-js-sdk/src/matrix';

import { SessionStateKeys, setSessionStorageValue } from 'hooks/useSessionState';
import { logAuthLifecycle, sanitizeMatrixError } from 'modules/Authentication/sessionObservability';
import { isMatrixRefreshCircuitOpen } from 'modules/Authentication/utils/matrixTokenRefresh';

export type MatrixSessionLogoutHandlers = {
  stopClient: () => void;
  markSoftLogoutRecovery: () => void;
  redirectToSso: () => void | Promise<void>;
  hardLogout: () => void;
  /** Last-chance refresh before hard logout when SDK refresh already failed. */
  recoverSession?: () => Promise<boolean>;
};

export type MatrixSessionLogoutContext = {
  mx_device_id?: string;
};

const isSoftLogoutError = (error: MatrixError): boolean =>
  (error.data as { soft_logout?: boolean } | undefined)?.soft_logout === true;

export const handleMatrixSessionLoggedOut = async (
  error: MatrixError,
  handlers: MatrixSessionLogoutHandlers,
  context?: MatrixSessionLogoutContext
): Promise<void> => {
  const mxDeviceId = context?.mx_device_id;
  const softLogout = isSoftLogoutError(error);

  // Permanently-dead credential (breaker open): preserve local stores and re-authenticate via SSO
  // reusing the same device_id, instead of wiping crypto/state with a hard logout.
  const beginSsoRecovery = () => {
    logAuthLifecycle('Permanent refresh failure after SessionLoggedOut — preserve-stores SSO recovery', {
      domain: 'session',
      action: 'recovery',
      path: 'sso_soft',
      scenario: 'A',
      circuit_open: true,
      mx_device_id: mxDeviceId,
    });
    handlers.markSoftLogoutRecovery();
    handlers.stopClient();
    void handlers.redirectToSso();
  };

  const finalizeHardLogout = () => {
    // AC-006 / OD-2: record that this session was authenticated before the hard logout clears
    // cookies, so the guest registration path redirects to /auth instead of auto-registering a guest.
    setSessionStorageValue(SessionStateKeys.AUTH_HAD_SSO_SESSION, true);
    // AC-011 (F-05): surface the session-expired toast after the hard-logout reload lands on /auth.
    // useSessionLoggedOut reads this flag on the next mount and shows session_logged_out.
    setSessionStorageValue(SessionStateKeys.IS_SESSION_LOGGED_OUT, true);
    logAuthLifecycle('Matrix session hard logout after SessionLoggedOut', {
      domain: 'session',
      action: 'recovery',
      path: 'hard_logout',
      scenario: 'A',
      mx_device_id: mxDeviceId,
    });
    handlers.hardLogout();
  };

  if (softLogout) {
    logAuthLifecycle('Matrix soft logout — redirecting to SSO', {
      domain: 'session',
      action: 'session_logged_out',
      path: 'sso_soft',
      scenario: 'A',
      soft_logout: true,
      mx_device_id: mxDeviceId,
    });
    handlers.markSoftLogoutRecovery();
    handlers.stopClient();
    void handlers.redirectToSso();
    return;
  }

  if (handlers.recoverSession) {
    try {
      const recovered = await handlers.recoverSession();
      if (recovered) {
        logAuthLifecycle('Matrix session recovered via token refresh after SessionLoggedOut', {
          domain: 'session',
          action: 'recovery',
          path: 'silent_refresh',
          scenario: 'A',
          outcome: 'success',
          mx_device_id: mxDeviceId,
        });
        return;
      }
    } catch (recoveryError) {
      const sanitized = sanitizeMatrixError(recoveryError);
      logAuthLifecycle(
        'Session recovery refresh failed',
        {
          domain: 'session',
          action: 'session_logged_out',
          outcome: 'transient_fail',
          mx_device_id: mxDeviceId,
        },
        'warn',
        sanitized
      );
    }
  }

  // Recovery refresh failed. A permanently-dead credential (breaker open) is never deferred (INV-2):
  // route straight to preserve-stores SSO recovery.
  if (isMatrixRefreshCircuitOpen()) {
    beginSsoRecovery();
    return;
  }

  // AC-004: transient failure (breaker closed) with a refresh credential still in hand — the network
  // was simply down. Do not hard logout now; wait for connectivity and retry recovery once before
  // giving up. When there is no refresh token at all (recoverSession undefined) there is nothing to
  // defer, so fall through to the immediate hard logout below (unchanged).
  if (handlers.recoverSession) {
    const recoverSession = handlers.recoverSession;
    const retryRecoveryOnReconnect = async () => {
      // One-shot: remove first so flapping connectivity cannot fan out concurrent retries (AC-007/FM-08).
      window.removeEventListener('online', retryRecoveryOnReconnect);
      try {
        const recovered = await recoverSession();
        if (recovered) {
          logAuthLifecycle('Matrix session recovered on reconnect after deferred logout', {
            domain: 'session',
            action: 'recovery',
            path: 'silent_refresh',
            scenario: 'A',
            outcome: 'success',
            mx_device_id: mxDeviceId,
          });
          return;
        }
      } catch (recoveryError) {
        logAuthLifecycle(
          'Deferred session recovery refresh failed',
          {
            domain: 'session',
            action: 'session_logged_out',
            outcome: 'transient_fail',
            mx_device_id: mxDeviceId,
          },
          'warn',
          sanitizeMatrixError(recoveryError)
        );
      }

      // Second failure — give up. Breaker now open → SSO recovery; still closed → hard logout.
      if (isMatrixRefreshCircuitOpen()) {
        beginSsoRecovery();
      } else {
        finalizeHardLogout();
      }
    };

    window.addEventListener('online', retryRecoveryOnReconnect);
    logAuthLifecycle('Deferring hard logout after SessionLoggedOut — waiting for connectivity', {
      domain: 'session',
      action: 'session_logged_out',
      phase: 'deferred',
      scenario: 'A',
      circuit_open: false,
      mx_device_id: mxDeviceId,
    });
    return;
  }

  finalizeHardLogout();
};
